import { FC, useMemo } from 'react'
import { Formik, Form, Field, ErrorMessage, FormikHelpers } from 'formik'
import * as Yup from 'yup'
import { Modal } from 'react-bootstrap'
import Select from 'react-select'
import { KTSVG } from '../../../../../../_metronic/helpers'
import { CreateSystemAlertModalProps, AlertTypeOption, FormValues, UserOption } from './types'
import {
  useCreateSystemAlertMutation,
  useGetAllUserAccountQuery,
} from '../../../../../../services/cellcast'
import { toast } from 'react-toastify'
import DatePicker from 'react-datepicker'
import 'react-datepicker/dist/react-datepicker.css'
import { CKEditor } from '@ckeditor/ckeditor5-react'
import ClassicEditor from '@ckeditor/ckeditor5-build-classic'
import moment from 'moment-timezone'
import { useTimezone } from '../../../../../hooks/useTimezone'

const validationSchema = Yup.object().shape({
  title: Yup.string().required('Title is required'),
  type: Yup.object()
    .shape({ value: Yup.string(), label: Yup.string() })
    .nullable()
    .required('Type is required'),
  message: Yup.string().required('Message is required'),
  startAt: Yup.string().required('Start date is required'),
  endAt: Yup.string().required('End date is required'),
  target: Yup.string().required('Target is required'),
  recipients: Yup.array().when('target', {
    is: 'specific',
    then: Yup.array().min(1, 'Select at least one user').required('Recipients required'),
    otherwise: Yup.array().notRequired(),
  }),
  methods: Yup.array().min(1, 'Select at least one method').required('Method is required'),
})

const alertTypes: AlertTypeOption[] = [
  { value: 'info', label: 'Info' },
  { value: 'warning', label: 'Warning' },
  { value: 'danger', label: 'Alert/Danger' },
]

const methodOptions = [
  { value: 'inbox', label: 'Inbox' },
  { value: 'email', label: 'Email' },
  { value: 'banner', label: 'Banner' },
]

const CreateSystemAlertPopup: FC<CreateSystemAlertModalProps> = ({
  show,
  onClose,
  isLoading: externalLoading,
}) => {
  const [createSystemAlert, { isLoading }] = useCreateSystemAlertMutation()
  const { data: userData, isLoading: userLoading } = useGetAllUserAccountQuery({
    page: 1,
    size: Number.MAX_SAFE_INTEGER,
  })

  const { userTimezone } = useTimezone()

  const userOptions: UserOption[] = useMemo(
    () => userData?.data?.items?.map((u: any) => ({ value: u._id, label: u.email })) || [],
    [userData]
  )

  const initialValues: FormValues = {
    title: '',
    type: null,
    message: '',
    startAt: '',
    endAt: '',
    target: 'all',
    recipients: [],
    methods: ['banner'],
  }

  const handleSubmit = async (values: FormValues, { setSubmitting }: FormikHelpers<FormValues>) => {
    const payload = {
      title: values.title,
      message: values.message,
      type: values.type?.value || '',
      methods: values.methods,
      recipients: values.target === 'all' ? 'all' : values.recipients.map((u) => u.value),
      startAt: values.startAt,
      endAt: values.endAt,
    }
    try {
      const res = await createSystemAlert(payload)
      if ('data' in res) {
        toast.success(res.data?.message || 'System alert created successfully')
        onClose()
      } else if ('error' in res) {
        const errObj = res.error as any
        if (errObj.data?.message) {
          toast.error(errObj.data.message)
        } else {
          toast.error('Failed to create system alert')
        }
      }
    } catch (err: any) {
      toast.error(err?.message || 'Failed to create system alert')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <Modal
      id='kt_modal_create_system_alert'
      tabIndex={1}
      aria-hidden='true'
      dialogClassName='modal-dialog-centered mw-700px'
      className='vh-100'
      show={show}
      onHide={onClose}
      scrollabel={'true'}
    >
      <div className='modal-header'>
        <h2>Add System Alert</h2>
        <div className='btn btn-icon btn-sm' onClick={onClose}>
          <KTSVG className='svg-icon-1' path='/media/icons/duotune/arrows/arr061.svg' />
        </div>
      </div>
      <div className='modal-body px-20 py-5 admin_list_modals'>
        <Formik
          initialValues={initialValues}
          validationSchema={validationSchema}
          onSubmit={handleSubmit}
        >
          {({ values, setFieldValue, errors, touched, isSubmitting }) => (
            <Form>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Type</label>
                <Select
                  options={alertTypes}
                  value={values.type}
                  onChange={(option) => setFieldValue('type', option)}
                  classNamePrefix='react-select'
                  className={touched.type && errors.type ? 'is-invalid' : ''}
                  placeholder='Select Type'
                  isClearable
                />
                <ErrorMessage name='type'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Start Date</label>
                <DatePicker
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  selected={values.startAt ? new Date(values.startAt) : null}
                  onChange={(date: Date) => setFieldValue('startAt', date?.toISOString() || '')}
                  showTimeSelect
                  dateFormat='Pp'
                  placeholderText='Select start date'
                />
                <ErrorMessage name='startAt'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>End Date</label>
                <DatePicker
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  selected={values.endAt ? new Date(values.endAt) : null}
                  onChange={(date: Date) => setFieldValue('endAt', date?.toISOString() || '')}
                  showTimeSelect
                  dateFormat='Pp'
                  placeholderText='Select end date'
                  minDate={
                    values.startAt ? moment(values.startAt).toDate() : undefined
                  }
                  minTime={
                    // Only apply minTime if same day
                    values.startAt && values.endAt &&
                      moment(values.startAt).isSame(
                        moment(values.endAt),
                        'day'
                      )
                      ? moment(values.startAt).toDate()
                      : moment().startOf('day').toDate()
                  }
                  maxTime={moment().endOf('day').toDate()}
                />
                <ErrorMessage name='endAt'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Channel</label>
                <div className='d-flex gap-3'>
                  {methodOptions.map((opt) => (
                    <div className='form-check form-check-solid' key={opt.value}>
                      <input
                        type='checkbox'
                        className='form-check-input'
                        disabled={opt.value === 'banner'}
                        id={`method-${opt.value}`}
                        checked={values.methods.includes(opt.value)}
                        onChange={(e) => {
                          if (e.target.checked) {
                            setFieldValue('methods', [...values.methods, opt.value])
                          } else {
                            setFieldValue(
                              'methods',
                              values.methods.filter((m) => m !== opt.value)
                            )
                          }
                        }}
                      />
                      <label className='form-check-label' htmlFor={`method-${opt.value}`}>
                        {opt.label}
                      </label>
                    </div>
                  ))}
                </div>
                <ErrorMessage name='methods'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Recipients</label>
                <div className='d-flex gap-3 mb-2'>
                  <div className='form-check form-check-inline form-check-solid'>
                    <input
                      type='radio'
                      name='target'
                      value='all'
                      className='form-check-input me-2'
                      id='target-all'
                      checked={values.target === 'all'}
                      onChange={() => {
                        setFieldValue('target', 'all')
                        setFieldValue('recipients', [])
                      }}
                    />
                    <label className='form-check-label ms-2' htmlFor='target-all'>
                      All Users
                    </label>
                  </div>
                  <div className='form-check form-check-inline form-check-solid'>
                    <input
                      type='radio'
                      name='target'
                      value='specific'
                      className='form-check-input me-2'
                      id='target-specific'
                      checked={values.target === 'specific'}
                      onChange={() => {
                        setFieldValue('target', 'specific')
                      }}
                    />
                    <label className='form-check-label ms-2' htmlFor='target-specific'>
                      Specific Users
                    </label>
                  </div>
                </div>
                {values.target === 'specific' && (
                  <Select
                    isMulti
                    isLoading={userLoading}
                    options={userOptions}
                    value={values.recipients}
                    onChange={(option) => setFieldValue('recipients', option)}
                    classNamePrefix='react-select'
                    placeholder='Select user emails...'
                  />
                )}
                <ErrorMessage name='recipients'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Title</label>
                <Field
                  name='title'
                  className='form-control form-control-lg form-control-solid mb-3 mb-lg-0'
                  placeholder='Alert Title'
                />
                <ErrorMessage name='title'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='mb-3'>
                <label className='col-form-label fw-bold fs-6'>Message</label>
                <CKEditor
                  editor={ClassicEditor}
                  data={values.message}
                  onChange={(_, editor) => setFieldValue('message', editor.getData())}
                />
                <ErrorMessage name='message'>
                  {(msg) => <span className='text-danger'>{msg}</span>}
                </ErrorMessage>
              </div>
              <div className='modal-footer d-flex justify-content-end gap-2 mt-4'>
                <div id='kt_drawer_chat_close'>
                  <button
                    type='button'
                    className='btn btn-md gray_bg_purple'
                    onClick={onClose}
                    disabled={isSubmitting || isLoading || externalLoading}
                  >
                    Cancel
                  </button>
                </div>
                <button
                  type='submit'
                  className='btn btn-primary'
                  disabled={isSubmitting || isLoading || externalLoading}
                >
                  {isSubmitting || isLoading || externalLoading ? 'Saving...' : 'Save'}
                </button>
              </div>
            </Form>
          )}
        </Formik>
      </div>
    </Modal>
  )
}

export default CreateSystemAlertPopup
